/**
 * Song Library adapter for the generic Bulk Operation Results dialog.
 * Parses archive/restore/PD errors into BulkOperationFailure rows.
 */

import { formatEntityRef } from "@/lib/entityRef";
import {
  BulkOperationFailure,
  BulkOperationFailureCategory,
  BulkOperationResultsSummary,
  buildBulkOperationResultsSummary,
  categoryDisplayLabel,
} from "@/features/bulk-operation-results";

export type TrackLifecycleBulkAction = "archive" | "restore" | "permanentDelete";

/** @deprecated Use BulkOperationResultsSummary via toSongBulkResultsSummary */
export type TrackLifecycleBulkSummary = BulkOperationResultsSummary;

export interface ContinuityBlockerLike {
  code?: string;
  message?: string;
  channelId?: string;
  channelName?: string;
  rotationId?: string;
  rotationName?: string;
  scheduleSide?: string;
  scheduleId?: string;
}

export interface SongIdentityHint {
  title?: string | null;
  artistName?: string | null;
}

export function categoryLabelFor(code: BulkOperationFailureCategory): string {
  return categoryDisplayLabel(code);
}

export function nextActionFor(
  category: BulkOperationFailureCategory,
  retryable: boolean,
): { short: string; detail: string } {
  switch (category) {
    case "continuity-blocked":
      return {
        short: "Add replacement music",
        detail:
          "Add replacement music to the affected Channel/rotation, then retry Archive or Permanent Delete.",
      };
    case "channel-cleanup-incomplete":
    case "programming-references-remain":
      return {
        short: "Retry after cleanup settles.",
        detail:
          "Retry this Song. If it fails again, check Channel Programming cleanup and try once more.",
      };
    case "auth":
      return {
        short: "Log out and back in, then retry.",
        detail: "Log out and back in, then retry the operation.",
      };
    case "network":
      return {
        short: "Safe to retry.",
        detail:
          "The request never completed, so this Song was not changed. Retry the operation.",
      };
    case "not-found":
      return {
        short: "Refresh Song Library.",
        detail: "Refresh Song Library. This Song may already be gone.",
      };
    case "dependency":
      return {
        short: "Resolve dependency, then retry.",
        detail: "Resolve the listed dependency in Song Library, then retry.",
      };
    default:
      return retryable
        ? {
            short: "Retry after review.",
            detail: "Retry this Song after checking the failure reason.",
          }
        : {
            short: "Review before retrying.",
            detail: "Review the failure reason before trying again.",
          };
  }
}

/** Sanitized operator-facing reason for transport/infrastructure failures. */
export const NETWORK_SERVICE_ERROR_REASON =
  "The request did not complete because the service was temporarily unavailable.";

/**
 * Gateway/service boilerplate bodies (e.g. API Gateway 5xx) — these carry no
 * application failure reason, so treat them like a transport failure.
 */
const GATEWAY_BOILERPLATE_MESSAGES = new Set([
  "internal server error",
  "service unavailable",
  "bad gateway",
  "gateway timeout",
  "endpoint request timed out",
  "network error",
]);

/**
 * Fetch/transport-style failures where the request never produced a usable
 * application response: RTK FETCH_ERROR / TIMEOUT_ERROR, PARSING_ERROR with
 * no usable payload, or 5xx with no application error body. Only consulted
 * when the error carries no application failure code — known backend codes
 * always keep their specific categories.
 */
function isTransportFailure(args: {
  rawStatus: unknown;
  httpStatus: number | null;
  message: string;
}): boolean {
  const s = typeof args.rawStatus === "string" ? args.rawStatus : null;
  if (s === "FETCH_ERROR" || s === "TIMEOUT_ERROR") return true;
  const message = args.message.trim().toLowerCase();
  if (s === "PARSING_ERROR") {
    // Non-JSON body (gateway/HTML error page) — no usable server response.
    return message === "";
  }
  if (args.httpStatus != null && args.httpStatus >= 500) {
    return message === "" || GATEWAY_BOILERPLATE_MESSAGES.has(message);
  }
  return false;
}

/** Channel Programming cleanup failure codes from track-channel-cleanup / archive. */
const CHANNEL_CLEANUP_CODES = new Set([
  "channel-cleanup-incomplete",
  "cleanup-incomplete",
  "memberships-remain",
  "membership-remove-failed",
  "membership-verify-failed",
  "membership-resolve-failed",
  "continuity-check-failed",
]);

export function mapFailureCategory(code: string | undefined | null): BulkOperationFailureCategory {
  const c = (code || "").toLowerCase().trim();
  if (c === "continuity-blocked" || c === "rotation-empty-after-removal") {
    return "continuity-blocked";
  }
  if (
    CHANNEL_CLEANUP_CODES.has(c) ||
    c.includes("channel-cleanup") ||
    c.includes("cleanup-incomplete") ||
    (c.includes("membership") &&
      (c.includes("remain") || c.includes("fail") || c.includes("verify") || c.includes("resolve")))
  ) {
    return "channel-cleanup-incomplete";
  }
  if (c === "programming-references-remain") {
    return "programming-references-remain";
  }
  if (c.includes("auth") || c.includes("forbidden") || c.includes("unauthorized")) {
    return "auth";
  }
  if (c.includes("not-found") || c === "not_found") {
    return "not-found";
  }
  if (c.includes("depend")) {
    return "dependency";
  }
  if (c.includes("conflict") || c.includes("concurrent")) {
    return "conflict";
  }
  // Last-resort fallback — flag so unmapped codes can be eliminated over time.
  // eslint-disable-next-line no-console
  console.warn("[bulk-operation-results] unmapped failure code → Other Failure", {
    code: c || null,
  });
  return "other";
}

function extractRemainingMembershipCount(
  message: string,
  details?: Record<string, unknown>,
): number | null {
  if (typeof details?.remaining === "number" && Number.isFinite(details.remaining)) {
    return Math.max(0, Math.floor(details.remaining));
  }
  const m = message.match(/(\d+)\s+membership/i);
  if (m) {
    const n = Number(m[1]);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Concise Reason-column text. Prefer a short operator-facing line derived from
 * the API code/message; fall back to the category label only when needed.
 */
export function conciseOperatorReason(args: {
  category: BulkOperationFailureCategory;
  code?: string | null;
  message: string;
  details?: Record<string, unknown>;
}): string {
  const code = (args.code || "").toLowerCase().trim();
  const message = (args.message || "").trim();
  const categoryLabel = categoryLabelFor(args.category);

  if (args.category === "channel-cleanup-incomplete") {
    if (code === "memberships-remain" || /memberships?\s+still\s+active/i.test(message)) {
      const n = extractRemainingMembershipCount(message, args.details);
      if (n != null) {
        const noun = n === 1 ? "membership" : "memberships";
        return `Channel cleanup incomplete — ${n} ${noun} still active`;
      }
      return "Channel cleanup incomplete — memberships still active";
    }
    if (code === "membership-remove-failed") {
      const rest = message
        .replace(/^Channel membership cleanup incomplete:\s*/i, "")
        .trim();
      return rest
        ? `Channel cleanup incomplete — ${rest}`
        : "Channel cleanup incomplete — membership remove failed";
    }
    if (code === "membership-verify-failed") {
      return "Channel cleanup incomplete — could not verify memberships were cleared";
    }
    if (code === "membership-resolve-failed") {
      return "Channel cleanup incomplete — could not resolve channel memberships";
    }
    if (code === "continuity-check-failed") {
      return "Channel cleanup incomplete — continuity check failed";
    }
    if (message) {
      return message
        .replace(/^Channel cleanup incomplete:\s*/i, "Channel cleanup incomplete — ")
        .replace(/\s+/g, " ")
        .trim();
    }
    return categoryLabel;
  }

  // Continuity / known categories: keep the short branded category in the table;
  // full API / blocker text lives in reasonDetail.
  if (args.category !== "other") {
    return categoryLabel;
  }

  // Unmapped: surface the API message when present so operators are not blind.
  if (message && message !== "Request failed") {
    const tightened = message.replace(/\s+/g, " ").trim();
    return tightened.length > 96 ? `${tightened.slice(0, 93)}…` : tightened;
  }
  return categoryLabel;
}

function listBlockers(
  details: Record<string, unknown> | undefined,
): ContinuityBlockerLike[] {
  const blockers = details?.blockers;
  if (!Array.isArray(blockers) || blockers.length === 0) return [];
  return blockers.filter(
    (b): b is ContinuityBlockerLike => !!b && typeof b === "object",
  );
}

function isLivingScheduleSide(side?: string | null): boolean {
  const s = String(side || "")
    .trim()
    .toLowerCase();
  return (
    s === "staging" ||
    s === "pending" ||
    s === "production" ||
    s === "live"
  );
}

/**
 * Continuity may block on multiple Schedule Sides / rotations for one Song
 * (Staging + Production copies). Prefer placement-scoped blockers so each
 * living side appears in Operation Results; fall back to the first blocker.
 * Detached / "unknown" sides are never operator-facing Schedule Sides.
 */
function placementBlockersForDisplay(
  details: Record<string, unknown> | undefined,
): ContinuityBlockerLike[] {
  const all = listBlockers(details);
  if (all.length === 0) return [];
  const placementScoped = all.filter(
    (b) =>
      (typeof b.scheduleSide === "string" && b.scheduleSide.trim() !== "") ||
      (typeof b.rotationId === "string" && b.rotationId.trim() !== "") ||
      (typeof b.rotationName === "string" && b.rotationName.trim() !== ""),
  );
  const candidates = placementScoped.length > 0 ? placementScoped : [all[0]];
  const living = candidates.filter((b) => {
    if (b.scheduleSide == null || String(b.scheduleSide).trim() === "") {
      return true;
    }
    if (isLivingScheduleSide(b.scheduleSide)) return true;
    // eslint-disable-next-line no-console
    console.error(
      "[schedule-side] dropping non-living continuity blocker from operator UI",
      {
        scheduleSide: b.scheduleSide,
        scheduleId: b.scheduleId || null,
        channelId: b.channelId || null,
        rotationId: b.rotationId || null,
      },
    );
    return false;
  });
  return living;
}

function toFailure(args: {
  trackId: string;
  identity?: SongIdentityHint | null;
  category: BulkOperationFailureCategory;
  code?: string | null;
  reasonDetail: string;
  channel: string | null;
  rotation: string | null;
  scheduleSide: string | null;
  retryable: boolean;
  details?: Record<string, unknown>;
}): BulkOperationFailure {
  const next = nextActionFor(args.category, args.retryable);
  const artist =
    args.identity?.artistName && args.identity.artistName !== "—"
      ? args.identity.artistName
      : null;
  const title =
    args.identity?.title ||
    formatEntityRef("track", args.trackId) ||
    null;
  return {
    id: args.trackId,
    first: artist,
    second: title,
    idLabel: formatEntityRef("track", args.trackId),
    category: args.category,
    reasonLabel: conciseOperatorReason({
      category: args.category,
      code: args.code,
      message: args.reasonDetail,
      details: args.details,
    }),
    reasonDetail: args.reasonDetail,
    channel: args.channel,
    rotation: args.rotation,
    scheduleSide: args.scheduleSide,
    retryable: args.retryable,
    nextAction: next.short,
    nextActionDetail: next.detail,
  };
}

function parseFailureContext(
  trackId: string,
  err: unknown,
  identity?: SongIdentityHint | string | null,
): {
  hint: SongIdentityHint | null;
  details: Record<string, unknown>;
  category: BulkOperationFailureCategory;
  code: string | null;
  message: string;
  retryable: boolean;
} {
  const hint: SongIdentityHint | null =
    typeof identity === "string"
      ? { title: identity }
      : identity && typeof identity === "object"
        ? identity
        : null;

  const anyErr = (err && typeof err === "object" ? err : {}) as Record<string, unknown>;
  const data = (anyErr.data && typeof anyErr.data === "object"
    ? anyErr.data
    : {}) as Record<string, unknown>;
  const details = (data.details && typeof data.details === "object"
    ? data.details
    : {}) as Record<string, unknown>;
  const status = typeof anyErr.status === "number" ? anyErr.status : null;
  const codeRaw =
    (typeof details.code === "string" && details.code) ||
    (typeof details.reasonCode === "string" && details.reasonCode) ||
    (status === 401 || status === 403 ? "auth" : null) ||
    (status === 404 ? "not-found" : null) ||
    null;
  const rawMessage =
    (typeof data.message === "string" && data.message) ||
    (typeof anyErr.message === "string" && anyErr.message) ||
    "";

  // Transport/infrastructure failures (request never produced a usable app
  // response). Never applied when a known application code is present.
  if (
    !codeRaw &&
    isTransportFailure({
      rawStatus: anyErr.status,
      httpStatus: status,
      message: rawMessage,
    })
  ) {
    // Diagnostics only — operators see the sanitized reason, not internals.
    // eslint-disable-next-line no-console
    console.warn(
      "[bulk-operation-results] transport failure → Network / Service Error",
      { status: anyErr.status ?? null },
    );
    return {
      hint,
      details,
      category: "network",
      code: null,
      message: NETWORK_SERVICE_ERROR_REASON,
      retryable: true,
    };
  }

  const category = mapFailureCategory(codeRaw);
  const message = rawMessage || "Request failed";
  const retryable =
    typeof details.retryable === "boolean"
      ? details.retryable
      : category !== "continuity-blocked" && category !== "not-found";
  return { hint, details, category, code: codeRaw, message, retryable };
}

function failuresFromContinuityDetails(args: {
  trackId: string;
  identity?: SongIdentityHint | null;
  details: Record<string, unknown>;
  category: BulkOperationFailureCategory;
  message: string;
  retryable: boolean;
}): BulkOperationFailure[] {
  const blockers = placementBlockersForDisplay(args.details);
  const code =
    typeof args.details.code === "string" ? args.details.code : "continuity-blocked";
  if (blockers.length === 0) {
    return [
      toFailure({
        trackId: args.trackId,
        identity: args.identity,
        category: args.category,
        code,
        reasonDetail: args.message,
        channel: null,
        rotation: null,
        scheduleSide: null,
        retryable: args.retryable,
        details: args.details,
      }),
    ];
  }
  return blockers.map((blocker) =>
    toFailure({
      trackId: args.trackId,
      identity: args.identity,
      category: args.category,
      code: (typeof blocker.code === "string" && blocker.code) || code,
      reasonDetail:
        (typeof blocker.message === "string" && blocker.message) || args.message,
      channel: blocker.channelName || null,
      rotation: blocker.rotationName || null,
      scheduleSide: blocker.scheduleSide || null,
      retryable: args.retryable,
      details: args.details,
    }),
  );
}

/**
 * Parse an RTK Query / fetch error from archive/restore into bulk failure
 * row(s). Continuity blockers expand to one row per Schedule Side / rotation
 * so Staging + Production copies both appear in Operation Results.
 */
export function parseTrackLifecycleBulkFailures(
  trackId: string,
  err: unknown,
  identity?: SongIdentityHint | string | null,
): BulkOperationFailure[] {
  const ctx = parseFailureContext(trackId, err, identity);
  if (ctx.category === "continuity-blocked") {
    return failuresFromContinuityDetails({
      trackId,
      identity: ctx.hint,
      details: ctx.details,
      category: ctx.category,
      message: ctx.message,
      retryable: ctx.retryable,
    });
  }
  // Non-continuity: never invent Schedule/Channel/Rotation from unrelated details.
  return [
    toFailure({
      trackId,
      identity: ctx.hint,
      category: ctx.category,
      code: ctx.code,
      reasonDetail: ctx.message,
      channel: null,
      rotation: null,
      scheduleSide: null,
      retryable: ctx.retryable,
      details: ctx.details,
    }),
  ];
}

/** @deprecated Prefer parseTrackLifecycleBulkFailures (multi-placement). */
export function parseTrackLifecycleBulkFailure(
  trackId: string,
  err: unknown,
  identity?: SongIdentityHint | string | null,
): BulkOperationFailure {
  return parseTrackLifecycleBulkFailures(trackId, err, identity)[0];
}

/** Map Permanent Delete bulk failure rows into the shared model (may be multi-row). */
export function failuresFromPermanentDeleteRow(
  row: {
    trackId: string;
    title?: string | null;
    message: string;
    retryable: boolean;
    details?: unknown;
  },
  identity?: SongIdentityHint | null,
): BulkOperationFailure[] {
  const details =
    row.details && typeof row.details === "object"
      ? (row.details as Record<string, unknown>)
      : undefined;
  const code = typeof details?.code === "string" ? details.code : null;
  const category = mapFailureCategory(code);
  const retryable =
    typeof details?.retryable === "boolean" ? details.retryable : row.retryable;
  const identityHint: SongIdentityHint = {
    title: identity?.title ?? row.title ?? null,
    artistName: identity?.artistName ?? null,
  };
  const message = row.message || "Permanent delete failed";

  if (category === "continuity-blocked") {
    return failuresFromContinuityDetails({
      trackId: row.trackId,
      identity: identityHint,
      details: details || {},
      category,
      message,
      retryable,
    });
  }

  // Non-continuity PD failures: no inferred placement columns.
  return [
    toFailure({
      trackId: row.trackId,
      identity: identityHint,
      category,
      code,
      reasonDetail: message,
      channel: null,
      rotation: null,
      scheduleSide: null,
      retryable,
      details,
    }),
  ];
}

/** @deprecated Prefer failuresFromPermanentDeleteRow (multi-placement). */
export function failureFromPermanentDeleteRow(
  row: {
    trackId: string;
    title?: string | null;
    message: string;
    retryable: boolean;
    details?: unknown;
  },
  identity?: SongIdentityHint | null,
): BulkOperationFailure {
  return failuresFromPermanentDeleteRow(row, identity)[0];
}

export function buildTrackLifecycleBulkSummary(args: {
  action: TrackLifecycleBulkAction;
  requested: number;
  succeeded: number;
  skipped: number;
  failures: BulkOperationFailure[];
}): BulkOperationResultsSummary {
  return buildBulkOperationResultsSummary({
    actionLabel: actionVerbPast(args.action),
    operationKey: args.action,
    entityNoun: "Song",
    entityNounPlural: "Songs",
    firstColumnLabel: "Artist",
    secondColumnLabel: "Title",
    showChannelColumns: true,
    requested: args.requested,
    succeeded: args.succeeded,
    skipped: args.skipped,
    failures: args.failures,
  });
}

export function actionVerbPast(action: TrackLifecycleBulkAction): string {
  if (action === "archive") return "Archived";
  if (action === "restore") return "Restored";
  return "Permanently deleted";
}

/** Re-export for callers that still import grouping/report from this module. */
export {
  formatBulkOperationFailureReport as formatBulkFailureReport,
  groupBulkOperationFailures as groupTrackLifecycleFailures,
} from "@/features/bulk-operation-results";
